import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  Badge,
  Card,
  EmptyState,
  Spinner,
} from '@wundo/ui';
import { listChatSessionMessages, listUserChatSessions } from '@/lib/users.ts';
import type { ChatSession, ChatSessionMessage } from '@/types/messages.ts';
import { chatSessionMessageText } from './chatSessionMessageText.ts';

export interface UserChatSessionsTabProps {
  userId: string;
}

function formatMoment(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Every conversation this person had, opened one at a time.
 *
 * A session's messages are only fetched once its row is opened: a hundred
 * sessions is a hundred requests nobody asked for, and the summary on the row
 * is usually the whole answer.
 */
export function UserChatSessionsTab({ userId }: UserChatSessionsTabProps) {
  const [openId, setOpenId] = useState<string | null>(null);

  const {
    data: sessions,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['users', userId, 'chat-sessions'],
    queryFn: () => listUserChatSessions(userId),
  });

  if (isPending) {
    return (
      <Card className='flex justify-center'>
        <Spinner />
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <EmptyState
          title='Failed to load chat sessions'
          description='Try again in a moment.'
        />
      </Card>
    );
  }

  if (sessions.length === 0) {
    return (
      <Card>
        <EmptyState title='No chat sessions found for this user.' />
      </Card>
    );
  }

  return (
    <Accordion
      multiple={false}
      value={openId === null ? [] : [openId]}
      onValueChange={(value: unknown[]) =>
        setOpenId((value[0] as string | undefined) ?? null)
      }
    >
      {sessions.map((session) => (
        <AccordionItem key={session.id} value={session.id}>
          <AccordionTrigger className='items-start'>
            <SessionSummary session={session} />
          </AccordionTrigger>
          <AccordionPanel>
            <div className='border-t border-line bg-fill-faint px-5.5 py-4'>
              {openId === session.id && <SessionMessages sessionId={session.id} />}
            </div>
          </AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

function SessionSummary({ session }: { session: ChatSession }) {
  return (
    <span className='flex min-w-0 flex-col gap-1 text-left'>
      <span className='truncate text-sm font-semibold text-ink'>
        {session.title || 'Untitled conversation'}
      </span>
      <span className='truncate text-sm font-normal text-ink-muted'>
        {session.summary || 'No summary available'}
      </span>
      <span className='flex items-center gap-3 text-xs font-normal text-ink-muted'>
        {formatMoment(session.started_at || session.created_at)}
        <Badge tone='quiet'>{`${session.message_count} messages`}</Badge>
      </span>
    </span>
  );
}

function SessionMessages({ sessionId }: { sessionId: string }) {
  const {
    data: messages,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['chat-sessions', sessionId, 'messages'],
    queryFn: () => listChatSessionMessages(sessionId),
  });

  if (isPending) {
    return (
      <p className='flex justify-center py-2'>
        <Spinner />
      </p>
    );
  }

  if (isError) {
    return (
      <p className='py-2 text-center text-sm text-danger'>Failed to load messages</p>
    );
  }

  if (messages.length === 0) {
    return (
      <p className='py-2 text-center text-sm text-ink-muted'>
        No messages found in this session.
      </p>
    );
  }

  return (
    <div className='flex max-h-96 flex-col gap-3 overflow-y-auto'>
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message} />
      ))}
    </div>
  );
}

/* Incoming is the person talking, so it sits on the side a reply comes from. */
function MessageBubble({ message }: { message: ChatSessionMessage }) {
  const fromUser = message.type === 'incoming_message';

  return (
    <div className={fromUser ? 'flex justify-end' : 'flex justify-start'}>
      <div
        className={
          fromUser
            ? 'max-w-[75%] rounded-lg bg-fill px-3 py-2 text-sm break-words whitespace-pre-wrap text-ink'
            : 'max-w-[75%] rounded-lg border border-line bg-surface px-3 py-2 text-sm break-words whitespace-pre-wrap text-ink'
        }
      >
        {chatSessionMessageText(message)}
        <p className='mt-1 text-xs text-ink-muted'>{formatMoment(message.created_at)}</p>
      </div>
    </div>
  );
}
